// src/components/SubscriptionPage.jsx
// Full subscription landing page with Monthly + Yearly plans.
// Links to Checkout.jsx (with plan selection) instead of direct Payment.
// Supports both standalone page and modal paywall mode.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import Payment from './payment';
import { useSubscription, PRICING, formatNaira } from '../hooks/useSubscription';
import toast from 'react-hot-toast';

const GOLD = '#B8860B';
const NAVY = '#001F3F';

// ── Plan card ─────────────────────────────────────────────────────────────────
function PlanCard({ planKey, plan, selected, onSelect }) {
  return (
    <div
      onClick={() => onSelect(planKey)}
      style={{
        ...planCard,
        border: selected ? `2px solid ${GOLD}` : '2px solid rgba(255,255,255,0.08)',
        background: selected ? 'rgba(184,134,11,0.1)' : 'rgba(255,255,255,0.02)',
        cursor: 'pointer',
      }}
    >
      {plan.badge && <div style={planBadgePill}>{plan.badge}</div>}
      <div style={{ color: GOLD, fontWeight: '900', fontSize: '0.75rem', letterSpacing: '2px', marginBottom: '8px' }}>
        {plan.label}
      </div>
      <div style={{ fontSize: '2rem', fontWeight: '900', color: 'white', marginBottom: '4px' }}>
        {formatNaira(plan.price)}
      </div>
      <div style={{ fontSize: '0.65rem', opacity: 0.5, marginBottom: '14px' }}>{plan.description}</div>
      <div style={featureRow}>✓ {plan.scansPerMonth} AI scans / month</div>
      <div style={featureRow}>✓ Unlimited style try-ons</div>
      <div style={featureRow}>✓ Full cranial health reports</div>
      <div style={featureRow}>✓ Live stylist sessions</div>
      {selected && (
        <div style={selectedTick}>✓ SELECTED</div>
      )}
    </div>
  );
}

// ── Benefits list ─────────────────────────────────────────────────────────────
const BENEFITS = [
  { icon: '🧬', title: 'Hyper3D Rodin Reconstruction', desc: 'Get 1 free photorealistic 3D head scan — your exact face, not a mannequin. Subscribe for monthly access: 4 scans/month (Monthly) or 6 scans/month (Yearly).' },
  { icon: '💇', title: 'Unlimited Style Try-Ons', desc: 'Try unlimited hairstyles on your 3D head every month, no cap.' },
  { icon: '📊', title: 'Full Biometric Health Vectors', desc: 'Facial symmetry, follicle density, jawline ratio — all computed monthly.' },
  { icon: '🔴', title: 'Live Stylist Sync', desc: 'Share your 3D scan link with your barber for real-time consultations.' },
  { icon: '🛡️', title: 'Escrow Shield for Bookings', desc: 'Pay barbers only after your cut is confirmed. Full dispute protection.' },
  { icon: '📥', title: 'Downloadable 3D GLB File', desc: 'Export your cranial solid for use in any 3D software or game engine.' },
];

// ── Free tier recap ────────────────────────────────────────────────────────────
function FreeRecap({ sub }) {
  if (sub.isSuperAdmin || sub.isSubscriptionActive) return null;
  return (
    <div style={freeBox}>
      <p style={{ color: GOLD, fontWeight: '700', fontSize: '0.65rem', letterSpacing: '1px', marginBottom: '10px' }}>
        YOUR FREE TIER STATUS
      </p>
 
      {/* CHANGE: was "/4", now "/1" — and threshold is >= 1, not >= 4 */}
      <div style={recapRow}>
        <span style={{ fontSize: '0.78rem' }}>Photorealistic 3D Head Scan</span>
        <span style={sub.lifetimeScansUsed >= 1 ? usedBadge : okBadge}>
          {sub.lifetimeScansUsed}/1 {sub.lifetimeScansUsed >= 1 ? 'USED' : 'REMAINING'}
        </span>
      </div>
 
      {/* Style try-on: unchanged at 1 */}
      <div style={recapRow}>
        <span style={{ fontSize: '0.78rem' }}>Style Try-On</span>
        <span style={sub.lifetimeStylesUsed >= 1 ? usedBadge : okBadge}>
          {sub.lifetimeStylesUsed}/1 {sub.lifetimeStylesUsed >= 1 ? 'USED' : 'REMAINING'}
        </span>
      </div>
 
      <div style={{ marginTop: '12px', fontSize: '0.62rem', opacity: 0.5, lineHeight: 1.6 }}>
        Subscribe for monthly or yearly access — 4–6 photorealistic scans + unlimited try-ons.
      </div>
    </div>
  );
}

// ── Unsubscribe section ───────────────────────────────────────────────────────
function UnsubscribeSection({ sub }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  if (!sub.isSubscriptionActive || sub.isSuperAdmin) return null;

  const endDate = sub.userData?.subscriptionEndDate;
  const endStr = endDate ? (endDate.toDate ? endDate.toDate() : new Date(endDate)).toLocaleDateString('en-NG', { day:'numeric', month:'long', year:'numeric' }) : 'Unknown';

  const handleUnsubscribe = async () => {
    if (!window.confirm('Cancel subscription? You will retain access until ' + endStr + ' and will not be billed again.')) return;
    setLoading(true);
    try {
      const { doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
      const { db } = await import('../firebase');
      await updateDoc(doc(db, 'users', sub.uid), {
        subscriptionStatus: 'cancelled',
        cancelledAt: serverTimestamp(),
      });
      toast.success('Subscription cancelled. Access continues until ' + endStr);
      navigate('/dashboard');
    } catch (e) {
      toast.error('Cancellation failed. Please contact support.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={unsubBox}>
      <p style={{ color: GOLD, fontWeight: '700', fontSize: '0.7rem', letterSpacing: '1px', marginBottom: '8px' }}>
        ACTIVE SUBSCRIPTION
      </p>
      <p style={{ fontSize: '0.78rem', opacity: 0.8, marginBottom: '4px' }}>
        {sub.tier?.toUpperCase()} PLAN · Renews {endStr}
      </p>
      <p style={{ fontSize: '0.72rem', opacity: 0.6, marginBottom: '16px' }}>
        {sub.PRICING[sub.tier]?.scansPerMonth} scans/month · Unlimited try-ons
      </p>
      <button
        onClick={handleUnsubscribe}
        disabled={loading}
        style={cancelSubBtn}
      >
        {loading ? 'PROCESSING...' : 'CANCEL SUBSCRIPTION'}
      </button>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function SubscriptionPage({ paywallMode = false, paywallReason = null, onClose = null }) {
  const navigate = useNavigate();
  const auth = getAuth();
  const user = auth.currentUser;
  const sub = useSubscription();
  const [selected, setSelected] = useState('yearly');
  const [showPayment, setShowPayment] = useState(false);

  const plan = PRICING[selected];

  // Copy when shown as a paywall modal
  const paywallHeadline = {
    free_exhausted: 'Your Free Scans Have Been Used',
    monthly_cap: 'Monthly Scan Limit Reached',
    style_exhausted: 'Free Style Try-On Used',
  }[paywallReason] || 'Upgrade Your Plan';

  const handleActivate = () => {
    if (!user) { 
      toast.error("Please log in to continue");
      navigate('/login'); 
      return; 
    }
    // Navigate to full checkout page with selected tier
    navigate('/checkout', { 
      state: { 
        selectedTier: selected 
      } 
    });
  };

  return (
    <div style={paywallMode ? overlayWrap : pageWrap}>
      <div style={paywallMode ? paywallCard : pageContainer}>
        {/* Close button when used as modal */}
        {paywallMode && onClose && (
          <button onClick={onClose} style={closeBtn}>✕</button>
        )}

        {/* Back button when used as standalone page */}
        {!paywallMode && (
          <button onClick={() => navigate('/dashboard')} style={backBtn}>← DASHBOARD</button>
        )}

        {/* Header */}
        <div style={headerBlock}>
          {paywallMode && <div style={{ fontSize: '2.5rem', marginBottom: '10px' }}>🔒</div>}
          <h1 style={title}>
            ESDRAS <span style={{ color: GOLD }}>SHIELD</span>
          </h1>
          <p style={subtitle}>
            {paywallMode ? paywallHeadline : 'Professional grooming security & biometric AI.'}
          </p>
        </div>

        {/* Free tier recap */}
        {!sub.loading && <FreeRecap sub={sub} />}

        {/* Active sub info + unsubscribe */}
        {!sub.loading && <UnsubscribeSection sub={sub} />}

        {/* Plan cards */}
        {(!sub.isSubscriptionActive || paywallMode) && (
          <>
            <div style={plansRow}>
              {Object.entries(PRICING).map(([key, p]) => (
                <PlanCard
                  key={key}
                  planKey={key}
                  plan={p}
                  selected={selected === key}
                  onSelect={setSelected}
                />
              ))}
            </div>

            {/* Benefits */}
            <div style={benefitsBox}>
              {BENEFITS.map((b, i) => (
                <div key={i} style={benefitItem}>
                  <span style={{ fontSize: '1.2rem', marginRight: '14px' }}>{b.icon}</span>
                  <div>
                    <strong style={{ display: 'block', fontSize: '0.85rem', color: GOLD }}>{b.title}</strong>
                    <span style={{ fontSize: '0.72rem', opacity: 0.65, lineHeight: '1.4' }}>{b.desc}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Button - Links to Checkout */}
            <button onClick={handleActivate} style={subscribeBtn}>
              CONTINUE TO PAYMENT — {formatNaira(plan.price)}
            </button>
            <p style={secureNote}>🔒 Secure payment via Monnify & Stripe • Cancel anytime</p>
          </>
        )}

        {/* Payment modal (kept for backward compatibility / paywall mode) */}
        {showPayment && (
          <Payment
            bookingId={user?.uid}
            amount={plan.price}
            email={user?.email}
            isSubscription={true}
            subscriptionTier={selected}
            onSuccess={() => {
              setShowPayment(false);
              toast.success(`${plan.label} SHIELD ACTIVATED 👑`);
              if (paywallMode && onClose) onClose();
              else navigate('/dashboard');
            }}
            onClose={() => setShowPayment(false)}
          />
        )}

        {/* Footer FAQ */}
        {!paywallMode && (
          <footer style={faqFooter}>
            <p>Can I cancel anytime? <strong>Yes.</strong></p>
            <p>Monthly plan: renews every 30 days.</p>
            <p>Yearly plan: renews every 12 months.</p>
            <p>Will I lose my 3D data if I cancel? <strong>No.</strong> Your scan stays in the vault.</p>
            <div style={{ marginTop: '20px', opacity: 0.3, fontSize: '0.6rem' }}>
              ESDRAS BIOMETRIC VAULT © 2026 · LAGOS, NIGERIA
            </div>
          </footer>
        )}
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const pageWrap = { minHeight: '100vh', background: '#050505', color: 'white', padding: '4rem 1rem', fontFamily: 'Montserrat, sans-serif', display: 'flex', flexDirection: 'column', alignItems: 'center' };
const overlayWrap = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px', overflowY: 'auto' };
const pageContainer = { width: '100%', maxWidth: '520px', color: 'white', fontFamily: 'Montserrat, sans-serif' };
const paywallCard = { background: 'linear-gradient(160deg,#05080f,#0d1f35)', border: `1px solid ${GOLD}44`, borderRadius: '28px', padding: '36px 28px', maxWidth: '500px', width: '100%', color: 'white', fontFamily: 'Montserrat, sans-serif', position: 'relative' };
const closeBtn = { position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: '1.2rem', cursor: 'pointer' };
const backBtn = { background: 'none', border: 'none', color: GOLD, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 'bold', marginBottom: '1.5rem', letterSpacing: '1px' };
const headerBlock = { textAlign: 'center', marginBottom: '28px' };
const title = { fontSize: '2.4rem', fontWeight: '900', margin: '0 0 8px', letterSpacing: '6px' };
const subtitle = { opacity: 0.6, fontSize: '0.9rem', fontWeight: '500' };
const plansRow = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '24px' };
const planCard = { borderRadius: '20px', padding: '22px 14px', textAlign: 'center', position: 'relative', transition: 'all 0.2s' };
const planBadgePill = { position: 'absolute', top: '-10px', left: '50%', transform: 'translateX(-50%)', background: GOLD, color: '#000', padding: '2px 12px', borderRadius: '10px', fontSize: '0.55rem', fontWeight: '900', letterSpacing: '1px', whiteSpace: 'nowrap' };
const featureRow = { fontSize: '0.62rem', color: 'rgba(255,255,255,0.7)', marginBottom: '4px' };
const selectedTick = { marginTop: '10px', color: GOLD, fontSize: '0.6rem', fontWeight: '900', letterSpacing: '1px' };
const freeBox = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px', padding: '14px 18px', marginBottom: '20px' };
const recapRow = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)', marginTop: '8px' };
const usedBadge = { background: 'rgba(255,77,77,0.15)', color: '#ff4d4d', border: '1px solid #ff4d4d44', borderRadius: '6px', padding: '2px 8px', fontSize: '0.6rem', fontWeight: '700' };
const okBadge = { background: 'rgba(0,255,136,0.12)', color: '#00ff88', border: '1px solid rgba(0,255,136,0.3)', borderRadius: '6px', padding: '2px 8px', fontSize: '0.6rem', fontWeight: '700' };
const benefitsBox = { background: `rgba(184,134,11,0.06)`, border: `1px solid rgba(184,134,11,0.2)`, borderRadius: '18px', padding: '20px 22px', marginBottom: '24px' };
const benefitItem = { display: 'flex', alignItems: 'flex-start', marginBottom: '18px' };
const subscribeBtn = { width: '100%', background: GOLD, color: 'black', border: 'none', padding: '1.4rem', borderRadius: '18px', fontSize: '1rem', fontWeight: '900', cursor: 'pointer', transition: 'all 0.3s ease', boxShadow: `0 15px 30px rgba(184,134,11,0.3)`, marginBottom: '12px' };
const secureNote = { fontSize: '0.65rem', opacity: 0.4, textAlign: 'center', letterSpacing: '1px', margin: 0 };
const unsubBox = { background: 'rgba(0,0,0,0.3)', border: `1px solid rgba(184,134,11,0.2)`, borderRadius: '14px', padding: '20px', marginBottom: '20px', textAlign: 'center' };
const cancelSubBtn = { background: 'transparent', color: '#ff4d4d', border: '1px solid rgba(255,77,77,0.4)', padding: '10px 24px', borderRadius: '50px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '700', letterSpacing: '1px' };
const faqFooter = { marginTop: '3rem', textAlign: 'center', fontSize: '0.8rem', opacity: 0.4, lineHeight: '2' };
